import Modal from 'components/Modal';
import Typography from 'components/Typography';
import styles from './CreatePurchaseOrderModal.module.scss';
import Select from 'components/Select';
import {useCreatePurchaseOrder} from './useCreatePurchaseOrder';
import Button from 'components/Button';
import NewCustomerModal from 'components/NewCustomerModal';
import CreateAddressModal from 'pages/UserForm/CreateAddressModal';
import CreatableSelect from 'components/CreatableSelect';
import ReactSwitch from 'react-switch';
import MissingItemsConfirmModal from './components/MissingItemsConfirmModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  dropoff_ids: number[];
  orderItems?: number[];
  onSuccess: () => void;
};

function CreatePurchaseOrderModal({
  isOpen,
  onClose,
  dropoff_ids,
  orderItems,
  onSuccess,
}: Props) {
  const {
    vendorOptions,
    selectedVendor,
    setSelectedVendor,
    getCustomerAddressesMutation,
    form,
    addressOptions,
    isCreatingPurchaseOrder,
    handleCreatePurchaseOrder,
    vendorModalOpen,
    setVendorModalOpen,
    addressModalOpen,
    setAddressModalOpen,
    refetchVendorList,
    purchaseOrderOptions,
    isAttachingPurchaseOrder,
    attachPickupOrder,
    setAttachPickupOrder,
    missingItems,
    showMissingItemsConfirmModal,
    setShowMissingItemsConfirmModal,
    attachPurchaseOrderToRequestMutation,
    sharedDefaultValue,
    setSharedDefaultValue,
    missingItemsErrorMessage,
  } = useCreatePurchaseOrder({
    dropoff_ids,
    orderItems,
    onCloseModal,
  });

  function onCloseModal() {
    onClose();
    form.reset();
    setSelectedVendor(null);
    onSuccess();
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        onRequestClose={onClose}
        className={styles.list_wrapper}
      >
        <Typography variant="h2">
          {attachPickupOrder
            ? 'Link to Existing Pickup Order'
            : 'Create New Pickup Order'}
        </Typography>
        <div className={styles.label}>
          <div>Vendor</div>
        </div>
        <CreatableSelect
          placeholder="Search For Vendor"
          components={{
            DropdownIndicator: () => null,
          }}
          onChange={(item) => {
            setSelectedVendor(item);
            getCustomerAddressesMutation(Number(item?.value));
          }}
          value={selectedVendor}
          menuPosition="fixed"
          options={vendorOptions}
          onCreateOption={(inputValue: string) => {
            setSharedDefaultValue(inputValue);
            setVendorModalOpen(true);
          }}
        />
        {Boolean(selectedVendor?.value) && (
          <>
            <div className={styles.label}>
              <div>Address</div>
            </div>
            <CreatableSelect
              placeholder="Search For Address"
              components={{
                DropdownIndicator: () => null,
              }}
              onChange={(item) => {
                form.setValue('address_id', Number(item?.value));
              }}
              value={
                addressOptions?.find(
                  (opt) => opt.value === form.watch('address_id')
                ) || null
              }
              menuPosition="fixed"
              options={addressOptions}
              onCreateOption={(inputValue: string) => {
                setSharedDefaultValue(inputValue);
                setAddressModalOpen(true);
              }}
            />
            <div className={styles.linkToExistingPickupOrderContainer}>
              <div className={styles.label} style={{marginTop: 11}}>
                <div>Link to Existing Pickup Order</div>
              </div>
              <ReactSwitch
                checked={attachPickupOrder}
                onChange={(checked) => setAttachPickupOrder(checked)}
                onColor="#4285F4"
                onHandleColor="#fff"
                handleDiameter={16}
                height={20}
                width={40}
                uncheckedIcon={false}
                checkedIcon={false}
              />
            </div>

            {attachPickupOrder && (
              <>
                <div className={styles.label}>
                  <div>Pickup Order</div>
                </div>
                <Select
                  placeholder="Click To Select"
                  onChange={(item) => {
                    form.setValue('pickup_id', Number(item?.value));
                  }}
                  value={
                    purchaseOrderOptions?.find(
                      (opt) => opt?.value === form.watch('pickup_id')
                    ) || null
                  }
                  menuPosition="absolute"
                  options={purchaseOrderOptions}
                />
              </>
            )}
          </>
        )}
        <Button
          loading={isCreatingPurchaseOrder || isAttachingPurchaseOrder}
          disabled={
            !selectedVendor ||
            !form.watch('address_id') ||
            isCreatingPurchaseOrder ||
            isAttachingPurchaseOrder
          }
          className={styles.addBtn}
          onClick={handleCreatePurchaseOrder}
        >
          {attachPickupOrder ? 'Link Order' : 'Create Order'}
        </Button>
      </Modal>
      {vendorModalOpen && (
        <NewCustomerModal
          title="Add New Vendor"
          sharedDefaultValue={sharedDefaultValue}
          onSuccess={(data) => {
            setSelectedVendor({
              value: data?.id,
              label: data?.user?.first_name + ' ' + data?.user?.last_name,
            });
            refetchVendorList();
          }}
          defaultClientRole="vendors"
          isOpen={vendorModalOpen}
          onClose={() => setVendorModalOpen(false)}
        />
      )}

      {addressModalOpen && (
        <CreateAddressModal
          title="Add New Vendor Address"
          newAddressType="dispatch"
          customer_id={Number(selectedVendor?.value)}
          handleClose={() => setAddressModalOpen(false)}
          sharedDefaultValue={sharedDefaultValue}
          onSuccess={(data) => {
            form.setValue('address_id', Number(data?.id));
            getCustomerAddressesMutation(Number(selectedVendor?.value));
          }}
          isOpen={addressModalOpen}
        />
      )}
      {showMissingItemsConfirmModal && (
        <MissingItemsConfirmModal
          missingItems={missingItems}
          isOpen={showMissingItemsConfirmModal}
          onClose={() => setShowMissingItemsConfirmModal(false)}
          isLoading={isAttachingPurchaseOrder}
          errorMessage={missingItemsErrorMessage}
          onConfirm={(autoCreateMissingItems: boolean) => {
            attachPurchaseOrderToRequestMutation({
              pickup_id: Number(form.watch('pickup_id')),
              dropoff_ids,
              auto_create_missing_items: autoCreateMissingItems,
            });
          }}
        />
      )}
    </>
  );
}
export default CreatePurchaseOrderModal;
